/*
 * SpecialsTemplatesPanel — save reusable Mon-Fri special combos and apply
 * them to any future week in one tap. Lives inside the parent
 * ScheduledSpecialsPanel so the owner sees "queue + templates" together.
 *
 * Weekend days (Sat/Sun) are intentionally not part of a template — those
 * are typically bespoke and get scheduled manually via the standard form.
 */
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Layers, Plus, Trash2, Loader2, Save, Calendar, X, Check, ChevronRight,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri"];

// Returns YYYY-MM-DD of the Monday of NEXT week (Brisbane-style local math).
function nextMondayISO() {
  const today = new Date();
  const dow = today.getDay(); // 0=Sun..6=Sat
  const daysUntilMon = ((7 - dow) % 7) || 7; // always move forward at least 1 week
  const mon = new Date(today);
  mon.setDate(today.getDate() + daysUntilMon);
  return mon.toISOString().slice(0, 10);
}

export default function SpecialsTemplatesPanel({ authHeaders, toast, dishOptions, dishNameById, onApplied }) {
  const [templates, setTemplates] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [applyingId, setApplyingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/admin/menu/specials/templates`, { headers: authHeaders });
      setTemplates(data.items || []);
    } catch (e) {
      toast({ title: "Load failed", description: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const remove = async (tpl) => {
    if (!window.confirm(`Delete template "${tpl.name}"?`)) return;
    setDeletingId(tpl.id);
    try {
      await axios.delete(`${API}/admin/menu/specials/templates/${tpl.id}`, { headers: authHeaders });
      toast({ title: "Template deleted" });
      load();
    } catch (e) {
      toast({ title: "Delete failed", description: e?.response?.data?.detail || e.message });
    } finally {
      setDeletingId(null);
    }
  };

  const applyToWeek = async (tpl, weekStart) => {
    setApplyingId(tpl.id);
    try {
      const { data } = await axios.post(
        `${API}/admin/menu/specials/templates/${tpl.id}/apply`,
        { week_start: weekStart },
        { headers: authHeaders },
      );
      const insertedCount = (data.inserted || []).length;
      const skippedCount = (data.skipped_past || []).length;
      toast({
        title: `Applied "${tpl.name}"`,
        description: skippedCount > 0
          ? `${insertedCount} day${insertedCount === 1 ? "" : "s"} scheduled · ${skippedCount} past day${skippedCount === 1 ? "" : "s"} skipped.`
          : `${insertedCount} day${insertedCount === 1 ? "" : "s"} scheduled starting ${weekStart}.`,
      });
      onApplied?.();
    } catch (e) {
      toast({ title: "Apply failed", description: e?.response?.data?.detail || e.message });
    } finally {
      setApplyingId(null);
    }
  };

  return (
    <div className="mt-5 pt-5 border-t border-[var(--mm-line)]" data-testid="specials-templates-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="section-label mb-1 flex items-center gap-2">
            <Layers size={13} /> Weekly Templates
          </div>
          <p className="text-xs text-[var(--mm-cream-muted)]">
            Save a Mon-Fri special combo and re-apply it to any future week in one tap.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          data-testid="tpl-new-btn"
          className="btn-outline inline-flex items-center gap-2 text-xs"
        >
          <Plus size={13} /> New template
        </button>
      </div>

      {busy ? (
        <div className="py-6 text-center"><Loader2 size={18} className="mx-auto text-[var(--mm-accent)] animate-spin" /></div>
      ) : templates.length === 0 ? (
        <div className="p-4 text-center border border-dashed border-[var(--mm-line)] text-[var(--mm-cream-muted)] text-xs">
          No templates yet. Create your first Mon-Fri combo above.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="tpl-list">
          {templates.map((tpl) => (
            <TemplateRow
              key={tpl.id}
              tpl={tpl}
              dishNameById={dishNameById}
              onDelete={() => remove(tpl)}
              onApply={(weekStart) => applyToWeek(tpl, weekStart)}
              applying={applyingId === tpl.id}
              deleting={deletingId === tpl.id}
            />
          ))}
        </ul>
      )}

      {showCreate && (
        <CreateTemplateModal
          dishOptions={dishOptions}
          authHeaders={authHeaders}
          toast={toast}
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); load(); }}
        />
      )}
    </div>
  );
}

function TemplateRow({ tpl, dishNameById, onDelete, onApply, applying, deleting }) {
  const [weekStart, setWeekStart] = useState(nextMondayISO());
  const byWeekday = useMemo(() => {
    const map = new Map();
    (tpl.days || []).forEach((d) => map.set(d.weekday, d));
    return map;
  }, [tpl]);

  return (
    <li className="border border-[var(--mm-line)] bg-[var(--mm-bg)] p-3" data-testid={`tpl-${tpl.id}`}>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-display italic text-lg text-[var(--mm-cream)] truncate">{tpl.name}</div>
          <div className="grid grid-cols-5 gap-1.5 mt-2">
            {WEEKDAYS.map((label, i) => {
              const d = byWeekday.get(i + 1);
              const name = d ? (dishNameById?.get?.(d.menu_item_id) || d.dish_name || `#${d.menu_item_id}`) : "—";
              return (
                <div key={label} className="border border-[var(--mm-line)] p-1.5 bg-[var(--mm-bg-alt)]/40">
                  <div className="text-[0.55rem] tracking-[0.22em] uppercase text-[var(--mm-cream-muted)]">{label}</div>
                  <div className="text-[0.68rem] text-[var(--mm-cream)] leading-tight line-clamp-2 mt-0.5">{name}</div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="flex flex-col gap-1.5 md:w-56 shrink-0">
          <input
            type="date"
            value={weekStart}
            onChange={(e) => setWeekStart(e.target.value)}
            data-testid={`tpl-week-${tpl.id}`}
            className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] text-xs px-2 py-1.5 focus:border-[var(--mm-accent)] outline-none"
            style={{ colorScheme: "dark" }}
            title="Monday of the target week"
          />
          <button
            onClick={() => onApply(weekStart)}
            disabled={applying}
            data-testid={`tpl-apply-${tpl.id}`}
            className="btn-primary inline-flex items-center justify-center gap-1.5 text-xs disabled:opacity-40"
          >
            {applying ? <Loader2 size={12} className="animate-spin" /> : <Calendar size={12} />}
            Apply to week
          </button>
          <button
            onClick={onDelete}
            disabled={deleting}
            data-testid={`tpl-delete-${tpl.id}`}
            className="text-[0.62rem] tracking-[0.22em] uppercase text-[var(--mm-cream-muted)] hover:text-red-400 inline-flex items-center justify-center gap-1"
          >
            <Trash2 size={11} /> Delete
          </button>
        </div>
      </div>
    </li>
  );
}

function CreateTemplateModal({ dishOptions, authHeaders, toast, onClose, onSaved }) {
  const [name, setName] = useState("Standard week");
  // days[i] = menu_item_id for weekday i+1 (Mon..Fri)
  const [days, setDays] = useState(["", "", "", "", ""]);
  const [saving, setSaving] = useState(false);

  const setDay = (i, val) => setDays((prev) => prev.map((v, idx) => (idx === i ? val : v)));

  const filledCount = days.filter((d) => d !== "").length;

  const save = async () => {
    if (!name.trim()) {
      toast({ title: "Name required", description: "Give this template a name so you can find it later." });
      return;
    }
    if (filledCount === 0) {
      toast({ title: "Pick at least one day", description: "Choose a dish for at least one weekday." });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        days: days
          .map((menu_item_id, i) => ({ weekday: i + 1, menu_item_id: menu_item_id === "" ? null : Number(menu_item_id) }))
          .filter((d) => d.menu_item_id != null),
      };
      await axios.post(`${API}/admin/menu/specials/templates`, payload, { headers: authHeaders });
      toast({ title: "Template saved", description: `"${name.trim()}" is ready to apply.` });
      onSaved?.();
    } catch (e) {
      toast({ title: "Save failed", description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="tpl-create-modal">
      <div className="w-full max-w-lg bg-[var(--mm-bg)] border border-[var(--mm-accent)] p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="section-label mb-1 flex items-center gap-2"><Layers size={13} /> New Template</div>
            <h3 className="font-display italic text-2xl text-[var(--mm-cream)]">Save a Mon-Fri combo</h3>
          </div>
          <button onClick={onClose} data-testid="tpl-modal-close" aria-label="Close" className="text-[var(--mm-cream)] hover:text-[var(--mm-accent)]">
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4">
          <label className="block">
            <div className="text-[0.62rem] tracking-[0.22em] uppercase text-[var(--mm-cream-muted)] mb-2">Template name</div>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              placeholder="Standard week"
              data-testid="tpl-name-input"
              className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] px-3 py-2 text-sm focus:border-[var(--mm-accent)] outline-none"
            />
          </label>

          <div className="space-y-2">
            <div className="text-[0.62rem] tracking-[0.22em] uppercase text-[var(--mm-cream-muted)]">Dishes by day</div>
            {WEEKDAYS.map((label, i) => (
              <div key={label} className="flex items-center gap-3">
                <div className="w-12 text-[0.62rem] tracking-[0.22em] uppercase text-[var(--mm-accent)] shrink-0">{label}</div>
                <select
                  value={days[i]}
                  onChange={(e) => setDay(i, e.target.value)}
                  data-testid={`tpl-day-${i + 1}`}
                  className="flex-1 bg-[var(--mm-bg-alt)] border border-[var(--mm-line)] text-[var(--mm-cream)] px-3 py-2 text-sm focus:border-[var(--mm-accent)] outline-none"
                >
                  <option value="">— skip this day —</option>
                  {dishOptions.map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </div>
            ))}
            <p className="text-[0.62rem] text-[var(--mm-cream-muted)] pt-1">
              {filledCount} of 5 days filled. Skipped days won&apos;t override existing schedules when the template is applied.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-[var(--mm-line)]">
          <button
            onClick={onClose}
            className="text-[0.62rem] tracking-[0.22em] uppercase text-[var(--mm-cream-muted)] hover:text-[var(--mm-cream)] px-3 py-2"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving || filledCount === 0}
            data-testid="tpl-save-btn"
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {saving ? "Saving..." : "Save template"}
          </button>
        </div>
      </div>
    </div>
  );
}
